<template>
  <div class="admin">
    <div class="admin__head">
      <div>
        <h1 class="admin__title">Administracija</h1>
        <div class="admin__sub">
          Pretraga svih predmeta, uključujući zatvorene. Faza se može promijeniti
          i zatvorenom predmetu.
        </div>
      </div>
    </div>

    <div class="admin__filters">
      <div class="search">
        <i class="el-icon-search search__icon"></i>
        <input
          v-model="searchTmp"
          class="search__input"
          placeholder="Pretraga: broj, oštećeni, registarska oznaka…"
          @change="applySearch"
          @keyup.enter="applySearch"
        />
        <i v-if="searchTmp" class="el-icon-close search__clear" @click="clearSearch"></i>
      </div>

      <el-select v-model="faza" class="filter-select" size="small" filterable @change="onFilterChange">
        <el-option v-for="o in phaseOptions" :key="o.value" :label="o.label" :value="o.value" />
      </el-select>

      <el-select
        v-model="obaveza"
        class="filter-select filter-select--wide"
        size="small"
        filterable
        placeholder="Obaveza"
        @change="onFilterChange"
      >
        <el-option v-for="o in obavezaOptions" :key="o.value" :label="o.label" :value="o.value" />
      </el-select>

      <span class="admin__hint">Kliknite predmet za promjenu faze</span>
    </div>

    <div class="admin__workspace">
      <PredmetList
        class="admin__list"
        :filter="{ status: { op: 'eq', val: faza } }"
        :search="search"
        :fobv="obaveza"
        :waitload="waitLoad"
        :selected-id="selected ? selected.id : 0"
        @select="onSelect"
      />

      <aside class="admin__panel">
        <div v-if="!selected" class="admin__empty">
          <div class="admin__empty-title">Odaberite predmet</div>
          <div class="admin__empty-sub">Kliknite red u listi</div>
        </div>
        <div v-else class="admin__panel-body">
          <div class="admin__panel-broj">Predmet {{ selected.broj }}</div>
          <StatusTag :value="selected.status" pill />
          <div class="admin__panel-meta">
            Zaprimljen {{ formatDate(selected.datum_prijema) }}
            <template v-if="selected.owner"> · {{ selected.owner.first_name }} {{ selected.owner.last_name }}</template>
          </div>
          <div class="admin__panel-actions">
            <el-button type="primary" class="w100" @click="phaseDialog = true">
              {{ selected.status === CLOSED_ID ? "Ponovo otvori" : "Promijeni fazu" }}
            </el-button>
            <el-button class="w100" @click="openPredmet">Otvori predmet</el-button>
          </div>
        </div>
      </aside>
    </div>

    <PhaseDialog
      v-if="selected"
      :visible.sync="phaseDialog"
      :predmet-id="selected.id"
      :predmet-broj="selected.broj"
      :current-status="selected.status"
      admin
      @updated="onUpdated"
    />
  </div>
</template>

<script>
import moment from "moment";
import API from "@/services/api/api";
import PredmetList from "@/components/PredmetList";
import PhaseDialog from "@/components/PhaseDialog";
import StatusTag from "@/components/StatusTag";
import { adminFilterOptions, ALL_PHASES, CLOSED_ID } from "@/models/status";

export default {
  name: "Admin",
  components: { PredmetList, PhaseDialog, StatusTag },
  data() {
    return {
      CLOSED_ID,
      waitLoad: true,
      searchTmp: "",
      search: "",
      faza: ALL_PHASES,
      obaveza: -1,
      selected: null,
      phaseDialog: false,
      phaseOptions: adminFilterOptions(),
      obavezaOptions: [{ value: -1, label: "Obaveza — sve" }],
      reloadKey: 0,
    };
  },
  methods: {
    formatDate(d) {
      return d ? moment(d).format("DD.MM.YYYY") : "";
    },
    onSelect(row) {
      this.selected = row;
    },
    onFilterChange() {
      this.selected = null;
    },
    applySearch() {
      this.search = this.searchTmp;
      this.selected = null;
    },
    clearSearch() {
      this.searchTmp = "";
      this.applySearch();
    },
    openPredmet() {
      this.$router.push("/predmet/view/" + this.selected.id);
    },
    // Re-run the current query so the row reflects its new phase.
    onUpdated() {
      const keep = this.search;
      this.selected = null;
      this.search = keep + " ";
      this.$nextTick(() => {
        this.search = keep;
      });
      this.$message({ message: "Faza je promijenjena.", type: "success" });
    },
    loadObaveza() {
      return API.Generic.all("obaveza")
        .then((data) => {
          this.obavezaOptions = [{ value: -1, label: "Obaveza — sve" }].concat(
            data.data.data.results.map((o) => ({ value: o.id, label: o.naziv }))
          );
        })
        .catch((err) => console.log(err));
    },
  },
  beforeMount() {
    this.waitLoad = false;
  },
  created() {
    this.waitLoad = true;
    this.loadObaveza();
  },
};
</script>

<style lang="scss" scoped>
.admin {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 10px;
}

.admin__head {
  flex-shrink: 0;
}

.admin__title {
  font-size: var(--fs-title);
  font-weight: 700;
  color: var(--ink);
  margin: 0;
}

.admin__sub {
  font-size: var(--fs-sm);
  color: var(--ink-3);
  margin-top: 4px;
}

.admin__filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.admin__hint {
  font-size: var(--fs-sm);
  color: var(--ink-4);
}

.search {
  position: relative;
  flex-grow: 1;
  max-width: 420px;
  display: flex;
  align-items: center;
}

.search__icon,
.search__clear {
  position: absolute;
  color: var(--ink-3);
  font-size: var(--fs-lg);
}

.search__icon {
  left: 11px;
}

.search__clear {
  right: 10px;
  cursor: pointer;
}

.search__input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 30px 8px 32px;
  border: 1px solid var(--line);
  background: var(--surface-alt);
  border-radius: var(--r-md);
  font: inherit;
  font-size: var(--fs-md);
  color: var(--ink);
  outline: none;

  &:focus {
    border-color: var(--brand);
    background: var(--surface);
    box-shadow: 0 0 0 3px rgba(47, 111, 237, 0.13);
  }

  &::placeholder {
    color: var(--ink-4);
  }
}

.filter-select {
  width: 230px;
}

.filter-select--wide {
  width: 220px;
}

.admin__workspace {
  display: flex;
  flex-grow: 1;
  min-height: 0;
}

.admin__list {
  flex-grow: 1;
  min-width: 0;
}

.admin__panel {
  width: 320px;
  flex-shrink: 0;
  background: var(--surface);
  border: 1px solid var(--line);
  border-left: none;
  border-radius: 0 var(--r-lg) var(--r-lg) 0;
  display: flex;
  flex-direction: column;
}

.admin__empty {
  margin: auto;
  text-align: center;
  padding: 20px;
}

.admin__empty-title {
  font-size: var(--fs-md);
  font-weight: 600;
  color: var(--ink-2);
}

.admin__empty-sub {
  font-size: var(--fs-sm);
  color: var(--ink-4);
  margin-top: 3px;
}

.admin__panel-body {
  padding: 18px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  height: 100%;
  box-sizing: border-box;
}

.admin__panel-broj {
  font-size: 17px;
  font-weight: 700;
  color: var(--ink);
}

.admin__panel-meta {
  font-size: var(--fs-sm);
  color: var(--ink-3);
}

.admin__panel-actions {
  margin-top: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.w100 {
  width: 100%;
  margin-left: 0 !important;
}
</style>
